import React, { useLayoutEffect, useState } from 'react'
import { graphql } from 'gatsby'
import { Heading } from '@toasttab/shared/src/components'
import Description from '@toasttab/shared/src/components/atoms/Description/Description'
import MainLayout from '@toasttab/shared/src/layout/MainLayout'
import { useSiteMetadata } from '@toasttab/main/src/hooks/use-site-metadata'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'
import Sites from '@toasttab/shared/src/data/Sites.json'
import { SiteSchema } from '@toasttab/shared/src/interfaces/Site.d'
import { PageContextProvider } from '@toasttab/shared/src/hooks/usePageContext'
import {
  useFooterContent,
  useHeaderContent
} from '@toasttab/shared/src/utils/layoutUtils'

export const query = graphql`
  query {
    allContentfulGlobalPageHeader {
      edges {
        node {
          internalName
          pageHeader {
            country
            ...HeaderContent
          }
        }
      }
    }
    allContentfulGlobalPageFooter {
      edges {
        node {
          internalName
          pageFooter {
            country
            ...FooterContent
          }
        }
      }
    }
    allContentfulGlobalSettings(filter: { node_locale: { eq: "en" } }) {
      edges {
        node {
          ...GlobalSettings
        }
      }
    }
    allContentfulMicrocopy(filter: { node_locale: { eq: "en" } }) {
      edges {
        node {
          key
          content
        }
      }
    }
    locales: allLocale {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`

const content = {
  title: 'Whoops! 404.',
  description: 'Sorry, the page you were looking for at this URL was not found.'
}
const NotFoundPage = ({ data }: any) => {
  const [siteId, setSiteId] = useState(null)

  const { t, language, region } = useI18next()
  const siteMetadata = useSiteMetadata()

  useLayoutEffect(() => {
    if (typeof window !== 'undefined') {
      let siteId = 1
      const langPath = window.location.pathname.substring(0, 3)
      Sites.sites.map(async (site: SiteSchema) => {
        if (site.url === langPath) {
          siteId = site.siteId
        }
      })
      setSiteId(siteId)
    }
  }, [])

  // Memoize header & footer content
  const headerData = data.allContentfulGlobalPageHeader?.edges?.find(
    (f: any) => f.node.internalName === 'Main Site Header'
  )?.node?.pageHeader
  const headerContent = useHeaderContent({
    headerData,
    region
  })
  const footerData = data.allContentfulGlobalPageFooter?.edges?.find(
    (f: any) => f.node.internalName === 'Main Site Footer'
  )?.node?.pageFooter
  const footerContent = useFooterContent({
    footerData,
    region
  })

  const localizedEntries = Sites.sites.map((site) => ({
    ...site,
    disabledRegionalSwitcherModal: true
  }))

  const context = {
    footerContent,
    globalSettings: data.allContentfulGlobalSettings?.edges[0]?.node,
    locales: data.locales?.edges,
    localizedEntries,
    microCopyData: data.allContentfulMicrocopy?.edges,
    siteMetadata
  }

  return (
    <>
      {siteId ? (
        <PageContextProvider value={context}>
          <MainLayout
            currentLanguage={language}
            footerContent={footerContent}
            headerContent={headerContent}
            localizedEntries={localizedEntries}
            packageName='main'
            siteId={siteId}
            siteMetadata={siteMetadata}
          >
            <section className='py-20 mb-20 min-h-[600px] '>
              <div className='flex flex-col items-center justify-center'>
                <Heading
                  classes='pb-3'
                  color={['text-default']}
                  content={t('404Title', { defaultValue: content.title })}
                  fontFamily='font-calibre'
                  fontSize={['text-6xl']}
                  fontWeight='font-semibold'
                  heading='h1'
                  textAlign={['text-center']}
                />
                <Description variation='otl-light-xl'>
                  {t('404Description', { defaultValue: content.description })}
                </Description>
              </div>
            </section>
          </MainLayout>
        </PageContextProvider>
      ) : null}
    </>
  )
}

export default NotFoundPage
